import {colorGroup, colorLabel, sortColors} from './evolved-device-color-order.js?v=20260925-1';
(() => {
  if (customElements.get('evolved-iphone-model-guide')) return;
  const normalize = value => String(value || '').trim().replace(/\s+/g, ' ').replace(/[″“”]/g, '"').replace(/\s*\/\s*/g, '/').replace(/(\d+(?:\/\d+)*) Gen\b/g, 'Gen $1').replace(/^(Samsung (?:Galaxy )?S\d+) Plus$/i, '$1+');
  const family = value => /^iPhone\s+(?:\d+[a-z]?|Duo|Air|Ultra|SE|XS|XR|X)\b/i.exec(value)?.[0] || (/iPad/i.test(value) ? 'iPad' : /Samsung|Galaxy/i.test(value) ? 'Samsung Galaxy' : /Watch|\d+\s*mm/i.test(value) ? 'Apple Watch' : value);
  const deviceFamily = value => /iPhone/i.test(value) ? 'iphone' : /iPad/i.test(value) ? 'ipad' : /Samsung|Galaxy/i.test(value) ? 'samsung' : /Watch|\d+\s*mm/i.test(value) ? 'watch' : '';
  const collectionFamilies = new Map([
    ['premium-magsafe-cases','iphone'], ['iphone-bumper-cases','iphone'],
    ['magsafe-iphone-cases-with-stand','iphone'], ['magsafe-essential-iphone-cases','iphone'],
    ['iphone-magsafe-cases','iphone'], ['ipad-cases','ipad'],
    ['apple-watch-bands','watch'], ['samsung-magsafe-cases','samsung']
  ]);
  class ModelGuide extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.controller = new AbortController();
      this.sectionPicker = this.hasAttribute('data-section-picker');
      this.collectionPicker = this.sectionPicker || this.hasAttribute('data-collection-picker');
      this.choice = this.querySelector('[data-model-choice]');
      this.colorChoice = this.querySelector('[data-color-choice]');
      this.status = this.querySelector('[data-model-status]');
      this.grid = this.querySelector('[data-model-grid]');
      this.originalControls = this.choice.getAttribute('aria-controls');
      this.cache = new Map();
      this.choice.addEventListener('change', () => this.showModel(), {signal:this.controller.signal});
      this.colorChoice?.addEventListener('change', () => this.showColor(), {signal:this.controller.signal});
      if (!this.sectionPicker) window.addEventListener('popstate', () => {
        const params = new URLSearchParams(location.search);
        const requested = normalize(params.get('model'));
        if ([...this.choice.options].some(option => option.value === requested)) this.choice.value = requested;
        this.pendingColor = colorGroup(params.get('color')).key;
        this.showModel();
      }, {signal:this.controller.signal});
      this.initialize();
    }
    disconnectedCallback() { this.request?.abort(); this.controller?.abort(); this.restoreSections(); this.started = false; }
    restoreSections() {
      this.browseSections?.forEach(section => section.classList.remove('ev-model-results-hidden'));
      this.categorySlots?.forEach(slot => {
        slot.section.classList.remove('ev-model-category-active');
        slot.grid.hidden = true;
        slot.grid.replaceChildren();
        slot.links.forEach(({link,href}) => link.setAttribute('href',href));
      });
      this.activeCategorySlots = [];
      if (this.originalControls) this.choice.setAttribute('aria-controls',this.originalControls);
      if (!this.sectionPicker) {
        const links = this.closest('.shopify-section')?.querySelector('evolved-collection-links');
        links?.classList.remove('ev-model-results-hidden');
        links?.buildLinks?.();
      }
    }
    categoryFamilies(section) {
      if (!this.productFamilies) this.productFamilies = new Map(this.products.map(product => [
        new URL(product.url,location.href).pathname,
        new Set(product.models.map(model=>deviceFamily(model.name)).filter(Boolean))
      ]));
      const families = new Set();
      if (section.querySelector('evolved-watch-bands')) families.add('watch');
      for (const link of section.querySelectorAll('a[href]')) {
        const url = new URL(link.getAttribute('href'),location.href);
        if (url.origin !== location.origin) continue;
        const handle = /\/collections\/([^/]+)\/?$/.exec(url.pathname)?.[1];
        if (collectionFamilies.has(handle)) families.add(collectionFamilies.get(handle));
        for (const name of this.productFamilies.get(url.pathname) || []) families.add(name);
      }
      return families;
    }
    prepareCategories() {
      if (this.categorySlots) return;
      const byPath = new Map(this.products.map(product => [new URL(product.url,location.href).pathname,String(product.id)]));
      this.categorySlots = this.browseSections.flatMap((section,index) => {
        const anchor = section.querySelector('[data-model-browse],evolved-watch-bands');
        if (!anchor) return [];
        const productIds = [...new Set([...section.querySelectorAll('a[href]')].flatMap(link => {
          const url = new URL(link.getAttribute('href'),location.href);
          const id = url.origin === location.origin ? byPath.get(url.pathname) : null;
          return id ? [id] : [];
        }))];
        const grid = document.createElement('div');
        grid.id = 'CategoryModels-' + this.dataset.section + '-' + index;
        grid.className = 'ev-model-guide__grid ev-guide__grid ev-category-model-grid';
        grid.hidden = true;
        anchor.after(grid);
        return [{section,grid,productIds,families:this.categoryFamilies(section),
          watchCards:[...section.querySelectorAll('.ev-watch-color-card')],
          links:[...section.querySelectorAll('.ev-view-all,.ev-guide__all,[data-watch-all]')]
            .map(link=>({link,href:link.getAttribute('href')}))}];
      });
    }
    categoryMessage(message) {
      this.activeCategorySlots?.forEach(slot => {
        const note = document.createElement('p');
        note.className = 'ev-category-model-message';
        note.textContent = message;
        slot.grid.replaceChildren(note);
        slot.grid.setAttribute('aria-busy','true');
      });
    }
    renderCards(cards,failed) {
      if (!this.hasAttribute('data-preserve-categories')) {
        this.grid.replaceChildren(...cards);
        return;
      }
      const byId = new Map(cards.map(card=>[card.dataset.guideProductId,card]));
      const model = this.choice.value, color = this.colorChoice?.value || '';
      const label = color ? this.colorChoice.selectedOptions[0].textContent + ' ' : '';
      for (const slot of this.activeCategorySlots) {
        const matches = slot.watchCards.length
          ? sortColors(slot.watchCards.filter(card=>normalize(card.dataset.guideModel)===model && (!color || colorGroup(card.dataset.guideColor).key===color))
            .map(card=>({card,name:card.dataset.guideColor})),model).map(item=>item.card)
          : slot.productIds.map(id=>byId.get(id)).filter(Boolean);
        slot.grid.replaceChildren(...matches.map(card=>card.cloneNode(true)));
        slot.grid.removeAttribute('aria-busy');
        if (!matches.length) {
          const note = document.createElement('p');
          note.className = 'ev-category-model-message';
          note.textContent = failed ? 'Products could not load. Select your model again to retry.'
            : 'No ' + label + 'options for ' + model + ' in this category. Try another color or model.';
          slot.grid.append(note);
        }
        for (const {link,href} of slot.links) {
          const url = new URL(href,location.href);
          if (slot.watchCards.length) {
            const hash = new URLSearchParams({'watch-size':/^\d+mm/.exec(model)?.[0] || model});
            if (color) hash.set('watch-color',color);
            url.hash = hash.toString();
          } else {
            url.searchParams.set('model',model);
            if (color) url.searchParams.set('color',color); else url.searchParams.delete('color');
          }
          link.setAttribute('href',url.pathname + url.search + url.hash);
        }
      }
    }
    setCollectionResults(active) {
      if (!this.collectionPicker) return;
      const ownSection = this.closest('.shopify-section');
      if (!this.browseSections) {
        this.browseSections = this.sectionPicker ? [...ownSection.querySelectorAll('[data-model-browse]')] : [...new Set([...document.querySelectorAll('#main [data-ev-product-rail], #main evolved-watch-bands, #main evolved-device-guide')].map(node => node.closest('.shopify-section')).filter(section => section && section !== ownSection))];
      }
      if (this.hasAttribute('data-preserve-categories')) {
        this.restoreSections();
        this.prepareCategories();
        if (active) {
          const selectedFamily = deviceFamily(this.choice.value);
          this.activeCategorySlots = this.categorySlots.filter(slot=>slot.families.has(selectedFamily));
          this.activeCategorySlots.forEach(slot=>{
            slot.section.classList.add('ev-model-category-active');
            slot.grid.hidden = false;
          });
          this.choice.setAttribute('aria-controls',this.activeCategorySlots.map(slot=>slot.grid.id).join(' '));
          this.categoryMessage('Loading matching products…');
        }
        this.grid.hidden = true;
        return;
      }
      this.browseSections.forEach(section => section.classList.toggle('ev-model-results-hidden',active));
      if (!this.sectionPicker) {
        const links = ownSection?.querySelector('evolved-collection-links');
        links?.classList.toggle('ev-model-results-hidden',active);
      }
      this.grid.hidden = !active;
    }
    async documentAt(url, signal) {
      const response = await fetch(url, {credentials:'same-origin', signal, headers:{Accept:'text/html'}});
      if (!response.ok) throw Error('Unable to load products');
      return new DOMParser().parseFromString(await response.text(), 'text/html');
    }
    async initialize() {
      try {
        let page = JSON.parse(this.querySelector('[data-model-catalogue]').textContent);
        const products = new Map(), visited = new Set();
        while (page) {
          for (const product of page.products) products.set(String(product.id), product);
          if (!page.next || visited.has(page.next)) break;
          visited.add(page.next);
          const url = new URL(page.next, location.href);
          url.searchParams.set('section_id', this.dataset.section);
          const doc = await this.documentAt(url, AbortSignal.timeout(12000));
          const data = doc.querySelector('[data-model-catalogue]');
          if (!data) throw Error('Unable to load the full catalogue');
          page = JSON.parse(data.textContent);
        }
        this.products = [...products.values()];
        const groups = new Map();
        for (const product of this.products) for (const model of product.models) {
          const name = normalize(model.name), group = family(name);
          if (!groups.has(group)) groups.set(group, new Set());
          groups.get(group).add(name);
        }
        let families = [...groups.keys()].sort((a,b) => {
          const number = value => Number(/^iPhone (\d+)/.exec(value)?.[1] || 0);
          return number(b) - number(a);
        });
        const duo = families.findIndex(name => /^iPhone Duo$/i.test(name));
        if (duo >= 0) {
          const [name] = families.splice(duo, 1), eighteen = families.indexOf('iPhone 18');
          families.splice(eighteen >= 0 ? eighteen + 1 : 0, 0, name);
        }
        this.choice.replaceChildren();
        if (this.collectionPicker) {
          const all = document.createElement('option'); all.value = ''; all.textContent = this.dataset.allLabel; this.choice.append(all);
        }
        for (const name of families) {
          const group = document.createElement('optgroup'); group.label = name;
          for (const model of groups.get(name)) {
            const option = document.createElement('option'); option.value = option.textContent = model; group.append(option);
          }
          this.choice.append(group);
        }
        const requested = this.sectionPicker ? '' : normalize(new URLSearchParams(location.search).get('model'));
        if (requested && [...this.choice.options].some(option => option.value === requested)) this.choice.value = requested;
        this.choice.disabled = this.choice.options.length <= (this.collectionPicker ? 1 : 0);
        if (this.choice.disabled) { this.status.textContent = this.collectionPicker ? 'No available models right now. Browse the products below.' : 'No available iPhone cases right now.'; return; }
        this.pendingColor = this.sectionPicker ? '' : colorGroup(new URLSearchParams(location.search).get('color')).key;
        await this.showModel();
      } catch (_) { this.status.textContent = this.collectionPicker ? 'Models could not load. Browse the products below or refresh to try again.' : 'Cases could not load. Refresh to try again, or browse all cases above.'; }
    }
    async cardFor(product, model, color, signal) {
      const url = new URL(product.url, location.origin);
      url.searchParams.set('section_id', 'evolved-model-card');
      url.searchParams.set('option_values', [model.id, color].filter(Boolean).join(','));
      const key = url.toString();
      let markup = this.cache.get(key);
      if (!markup) {
        const doc = await this.documentAt(url, signal), response = doc.querySelector('[data-model-response]');
        if (!response) throw Error('Unable to load the selected variant');
        markup = response.outerHTML; this.cache.set(key, markup);
      }
      const response = new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-model-response]');
      if (response.dataset.available !== 'true' || normalize(response.dataset.model) !== normalize(model.name)) return null;
      // Never substitute another color for an explicitly requested option.
      if (color && String(response.dataset.colorId) !== String(color)) return null;
      const card = response.querySelector('.ev-guide-card');
      if (!card) return null;
      card.dataset.guideModel = model.name;
      card.dataset.guideProductId = String(product.id);
      card.dataset.guideColor = response.dataset.colorName || '';
      const colors = JSON.parse(response.querySelector('[data-model-colors]')?.textContent || '[]');
      return {card, colors};
    }
    syncLocation() {
      if (this.sectionPicker) return;
      const url = new URL(location.href);
      for (const [key,value] of [['model',this.choice.value],['color',this.colorChoice?.value]]) {
        if (value) url.searchParams.set(key,value); else url.searchParams.delete(key);
      }
      history.replaceState(history.state,'',url);
    }
    resetColors(label) {
      if (!this.colorChoice) return;
      this.colorChoice.replaceChildren(new Option(label,'')); this.colorChoice.disabled = true;
    }
    async showModel() {
      this.request?.abort(); const request = new AbortController(); this.request = request;
      const chosen = this.choice.value;
      const wantedColor = this.pendingColor ?? this.colorChoice?.value ?? '';
      this.pendingColor = null; this.modelItems = [];
      if (this.collectionPicker && !chosen) {
        this.setCollectionResults(false); this.grid.replaceChildren(); this.grid.removeAttribute('aria-busy');
        this.resetColors('Choose a model first'); this.syncLocation();
        this.status.textContent = this.dataset.idleLabel; return;
      }
      this.setCollectionResults(true); this.resetColors('Loading colors…');
      const matches = this.products.flatMap(product => {
        const model = product.models.find(model => normalize(model.name) === chosen);
        return model ? [{product,model}] : [];
      });
      this.status.textContent = 'Finding products for ' + chosen + '…';
      this.grid.setAttribute('aria-busy','true'); this.grid.replaceChildren();
      let cursor = 0, failures = 0;
      const items = new Array(matches.length);
      const worker = async () => {
        while (cursor < matches.length && !request.signal.aborted) {
          const index = cursor++, {product,model} = matches[index];
          try {
            const result = await this.cardFor(product,model,null,request.signal);
            if (result) items[index] = {product,model,...result};
          } catch (error) { if (!request.signal.aborted) failures++; }
        }
      };
      await Promise.all(Array.from({length:Math.min(4,matches.length)},worker));
      if (request.signal.aborted) return;
      this.modelItems = items.filter(Boolean); this.baseFailures = failures;
      const colors = new Map();
      for (const item of this.modelItems) {
        const counted = new Set();
        for (const color of item.colors) {
          const group = colorGroup(color.name), existing = colors.get(group.key);
          if (existing) { existing.names.push(color.name); if (!counted.has(group.key)) existing.count++; }
          else colors.set(group.key,{...group,names:[color.name],count:1});
          counted.add(group.key);
        }
      }
      if (this.colorChoice) {
        this.colorChoice.replaceChildren(new Option('All colors',''),...sortColors([...colors.values()],chosen).map(color=>new Option(colorLabel(color),color.key)));
        this.colorChoice.disabled = !colors.size;
        this.colorChoice.value = colors.has(wantedColor) ? wantedColor : '';
      }
      await this.showColor();
    }
    async showColor() {
      this.request?.abort(); const request = new AbortController(); this.request = request;
      const chosen = this.choice.value, selected = this.colorChoice?.value || '';
      this.syncLocation();
      const items = this.modelItems || [];
      const matches = selected ? items.filter(item=>item.colors.some(color=>colorGroup(color.name).key===selected)) : items;
      let cards = matches.map(item=>item.card.cloneNode(true)), failures = this.baseFailures || 0;
      if (selected) {
        this.grid.setAttribute('aria-busy','true');
        this.categoryMessage('Loading matching products…');
        this.status.textContent = 'Finding ' + this.colorChoice.selectedOptions[0].textContent + ' for ' + chosen + '…';
        let cursor = 0; cards = new Array(matches.length);
        const worker = async () => {
          while (cursor < matches.length && !request.signal.aborted) {
            const index = cursor++, item = matches[index];
            const color = sortColors(item.colors.filter(color=>colorGroup(color.name).key===selected),chosen)[0];
            try { cards[index] = (await this.cardFor(item.product,item.model,color.id,request.signal))?.card; }
            catch (error) { if (!request.signal.aborted) failures++; }
          }
        };
        await Promise.all(Array.from({length:Math.min(4,matches.length)},worker));
      }
      if (request.signal.aborted) return;
      const visible = cards.filter(Boolean);
      this.renderCards(visible,failures); this.grid.removeAttribute('aria-busy');
      const colorLabel = selected ? this.colorChoice.selectedOptions[0].textContent + ' · ' : '';
      this.status.textContent = failures ? 'Some products could not load. Select your model again to retry.' : visible.length ? colorLabel + visible.length + ' matching ' + (visible.length===1?'product':'products') + ' for ' + chosen + '.' : 'No available products for this selection. Try another color or model.';
    }

  }
  customElements.define('evolved-iphone-model-guide', ModelGuide);
})();
